import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";
import logoNegativo from "@/assets/logo-equalitas-negativo.webp";
import logoIcam from "@/assets/logo-icam.webp";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, localePath } = useLocale();

  const legalLinks = [
    { href: localePath("/aviso-legal"), label: t("footer.legal_notice") },
    { href: localePath("/politica-privacidad"), label: t("footer.privacy") },
    { href: localePath("/politica-cookies"), label: t("footer.cookies") },
  ];

  const serviceLinks = [
    { href: localePath("/"), label: t("header.nav.home") },
    { href: localePath("/sobre-nosotros"), label: t("header.nav.about") },
    { href: localePath("/espacio-informativo"), label: t("header.nav.blog") },
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <img
              src={logoNegativo}
              alt="Equalitas Abogados - Especialistas en extranjería y regularización de extranjeros en España"
              className="h-20 w-auto mb-4"
              width={200}
              height={80}
            />
            <p className="text-primary-foreground/80 text-sm leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t("footer.contact")}</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>C/ Ríos Rosas 25, 1º izq. Madrid</span>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <a href="tel:+34915931141" className="hover:text-accent transition-colors">+34 91 593 11 41</a>
                  <a href="tel:+34650398298" className="hover:text-accent transition-colors">+34 650 398 298</a>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <a href="mailto:equalitasabogados@equalitasabogados.com" className="hover:text-accent transition-colors break-all">equalitasabogados@equalitasabogados.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span>{t("footer.schedule")}</span>
              </li>
            </ul>
          </div>

          {/* Quick Links — key pages for SEO internal linking */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t("footer.services")}</h3>
            <ul className="space-y-2 text-sm">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">{t("footer.legal")}</h3>
            <ul className="space-y-2 text-sm">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-sm text-primary-foreground/70">
          <div className="flex flex-col items-center gap-4">
            <img
              src={logoIcam}
              alt="Ilustre Colegio de la Abogacía de Madrid (ICAM)"
              className="h-16 w-auto opacity-85 flex-shrink-0"
              width={120}
              height={64}
            />
            <div className="text-center">
              <p>© {currentYear} {t("footer.copyright")}</p>
              <p className="mt-1">{t("footer.icam")}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
